import React, {
  createContext,
  FunctionComponent,
  ReactNode,
  useContext,
  useMemo,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';

import FormularElement from '../formBuilder/formularElement';

interface PublicRouteContextValue {
  isPublicRoute: boolean;
}

interface PublicRouteProviderProps {
  children: ReactNode;
  staticPagesConfig: FormularElement[];
}

const PublicRouteContext = createContext<PublicRouteContextValue>({
  isPublicRoute: false,
});

const normalizeRoute = (route: string): string => {
  const normalized = `/${route}`
    .replace(/\/+/g, '/')
    .replace(/\/$/, '')
    .toLowerCase();
  return normalized || '/';
};

export const isPublicStaticPageRoute = (
  pathname: string,
  project: string | undefined,
  staticPagesConfig: FormularElement[]
): boolean => {
  if (!project) return false;

  const normalizedPath = normalizeRoute(pathname);
  const projectPrefix = normalizeRoute(project);
  if (
    normalizedPath !== projectPrefix &&
    !normalizedPath.startsWith(`${projectPrefix}/`)
  )
    return false;

  const currentRoute = normalizedPath.slice(projectPrefix.length) || '/';
  return staticPagesConfig.some(
    (page) =>
      page.publicBeforeLogin === true &&
      Boolean(page.route) &&
      normalizeRoute(page.route ?? '') === currentRoute
  );
};

export const PublicRouteProvider: FunctionComponent<
  PublicRouteProviderProps
> = ({ children, staticPagesConfig }) => {
  const { pathname } = useLocation();
  const { project } = useParams();
  const isPublicRoute = isPublicStaticPageRoute(
    pathname,
    project,
    staticPagesConfig
  );
  const contextValue = useMemo(() => ({ isPublicRoute }), [isPublicRoute]);

  return (
    <PublicRouteContext.Provider value={contextValue}>
      {children}
    </PublicRouteContext.Provider>
  );
};

export const usePublicRoute = () => useContext(PublicRouteContext);

export default PublicRouteContext;
